import {
    CurrencyPipe,
    NgFor,
    NgIf,
    NgTemplateOutlet,
} from '@angular/common';
import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbPagination,
    NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
    faAngleLeft,
    faFileDownload,
    faRotateLeft,
    faSearch,
    faSortAmountDownAlt,
    faSortDown,
    faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import { Location } from '@angular/common';
import { Subject, debounceTime } from 'rxjs';
import { environment } from 'src/environments/environment';

import { ReportService } from '../report.service';
import { TableControlService } from '../table-control.service';
import { FormatDateTimePipe } from '../format-date-time.pipe';

// Update this path if your date picker controller is in a different folder
import { DatePickerControllerComponent } from '../date-picker-controller/date-picker-controller.component';
import { RouterLink } from '@angular/router';

@Component({
    selector: 'app-report-monthly-payments',
    templateUrl: './report-monthly-payments.component.html',
    styleUrls: ['./report-monthly-payments.component.css'],
    imports: [
        FormsModule,
        NgIf,
        NgFor,
        NgTemplateOutlet,
        CurrencyPipe,
        NgbTooltip,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        NgbDropdownItem,
        NgbPagination,
        FaIconComponent,
        FormatDateTimePipe,
        DatePickerControllerComponent,
        RouterLink
    ],
})
export class ReportMonthlyPaymentsComponent implements OnInit, AfterViewInit {
    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        this.resizeSubject.next((event.target as Window).innerWidth);
    }

    @ViewChild('datePickerController')
    datePickerController!: DatePickerControllerComponent;

    private resizeSubject: Subject<number> = new Subject<number>();

    allEntries: any[] = [];
    entries: any[] = [];
    entryCount = 0;
    isMobileView = false;

    searchTerm = '';

    // These stay in YYYY-MM-DD format for the date picker
    startDate = '';
    endDate = '';

    sortBy = 'paymentDate';
    sortDirection: 'ASC' | 'DESC' = 'DESC';

    tableControls: TableControlService;

    sortOptions = [
        { label: 'Client Name', value: 'clientName' },
        { label: 'Invoice Number', value: 'invoiceNumber' },
        { label: 'Proposal Subtotal', value: 'proposalSubtotal' },
        { label: 'Tax Rate', value: 'taxRate' },
        { label: 'Tax Name', value: 'proposalTaxName' },
        { label: 'Proposal Sales Tax', value: 'proposalSalesTax' },
        { label: 'Accepted Proposal', value: 'totalSale' },
        { label: 'Invoice Sales Tax', value: 'invoicedSalesTax' },
        { label: 'Invoice Amount', value: 'invoiceAmount' },
        { label: 'Amount Paid', value: 'amountPaid' },
        { label: 'Payment Type', value: 'paymentType' },
        { label: 'Payment Date', value: 'paymentDate' },
        { label: 'Balance Due', value: 'balanceDueOnProject' },
    ];

    faAngleLeft = faAngleLeft;
    faFileDownload = faFileDownload;
    faRotateLeft = faRotateLeft;
    faSearch = faSearch;
    faSortAmountDownAlt = faSortAmountDownAlt;
    faSortDown = faSortDown;
    faSortUp = faSortUp;

    get isMobile(): boolean {
        return this.isMobileView;
    }

    get totals() {
        return this.allEntries.reduce(
            (acc, entry) => {
                acc.invoiceAmount += Number(entry.invoiceAmount || 0);
                acc.invoicedSalesTax += Number(entry.invoicedSalesTax || 0);
                acc.amountPaid += Number(entry.amountPaid || 0);
                acc.proposalSubtotal += Number(entry.proposalSubtotal || 0);
                acc.proposalSalesTax += Number(entry.proposalSalesTax || 0);
                acc.totalSale += Number(entry.totalSales || 0);
                acc.balanceDueOnProject += Number(entry.balanceDueOnProject || 0);

                return acc;
            },
            {
                invoiceAmount: 0,
                invoicedSalesTax: 0,
                amountPaid: 0,
                proposalSubtotal: 0,
                proposalSalesTax: 0,
                totalSale: 0,
                balanceDueOnProject: 0,
            }
        );
    }

    constructor(
        private reportService: ReportService,
        public location: Location
    ) {
        this.resizeSubject.pipe(debounceTime(100)).subscribe((width) => {
            if (!this.tableControls) return;

            if (width <= 768) {
                this.checkScreenSize(width);
            } else {
                this.isMobileView = false;
                this.tableControls.setLimit(20);
            }

            this.applyLocalControls();
        });
    }

    ngOnInit(): void {
        this.tableControls = new TableControlService(
            'Reports.MonthlyPayments',
            true,
            'paymentDate',
            'DESC'
        );

        this.checkScreenSize();

        this.setDefaultCurrentMonthRange();

        this.tableControls.refresh.subscribe(() => {
            this.applyLocalControls();
        });

        this.getMonthlyPaymentsReport();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.datePickerController?.showPreset('CUR_MTH', false);
        });
    }

    private setDefaultCurrentMonthRange(): void {
        const today = new Date();

        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        this.startDate = this.formatDateToYYYYMMDD(firstDay);
        this.endDate = this.formatDateToYYYYMMDD(lastDay);
    }

    dateChanged(event: any): void {
        const normalizedStart = this.normalizeDatePickerValue(event?.startDate);
        const normalizedEnd = this.normalizeDatePickerValue(event?.endDate);

        if (!normalizedStart || !normalizedEnd) {
            return;
        }

        this.startDate = normalizedStart;
        this.endDate = normalizedEnd;

        this.tableControls.setCurrentPage(1);
        this.getMonthlyPaymentsReport();
    }

    private formatDateToYYYYMMDD(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    private normalizeDatePickerValue(value: any): string {
        if (!value) return '';

        if (value instanceof Date) {
            return this.formatDateToYYYYMMDD(value);
        }

        if (typeof value === 'string') {
            // Already date picker format: YYYY-MM-DD
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return value;
            }

            // MM/DD/YYYY to YYYY-MM-DD
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
                const [month, day, year] = value.split('/');
                return `${year}-${month}-${day}`;
            }

            return value;
        }

        // ng-bootstrap date object: { year, month, day }
        if (value.year && value.month && value.day) {
            const year = value.year;
            const month = String(value.month).padStart(2, '0');
            const day = String(value.day).padStart(2, '0');

            return `${year}-${month}-${day}`;
        }

        return '';
    }

    private formatDateForApi(date: string): string | null {
        if (!date) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date.split('-');
            return `${month}${day}${year}`;
        }

        if (/^\d{8}$/.test(date)) {
            return date;
        }

        return null;
    }

    private getMonthlyPaymentsReport(): void {
        const formattedStartDate = this.formatDateForApi(this.startDate);
        const formattedEndDate = this.formatDateForApi(this.endDate);

        if (!formattedStartDate || !formattedEndDate) {
            return;
        }

        const params: any = {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
        };

        this.reportService
            .getMonthlyPaymentsReport(params)
            .subscribe((reportData) => {
                const data = reportData.entries || reportData || [];

                this.allEntries = data.map((entry: any) => this.normalizeEntry(entry));
                this.applyLocalControls();
            });
    }

    private getEntryValue(entry: any, key: string): any {
        const fallbackMap: Record<string, string[]> = {
            clientName: ['clientName', 'client_name'],
            invoiceAmount: ['invoiceAmount', 'invoice_amount'],
            invoicedSalesTax: ['invoicedSalesTax', 'invoiced_sales_tax'],
            amountPaid: ['amountPaid', 'amount_paid'],
            paymentDate: ['paymentDate', 'payment_date'],
            proposalSubtotal: ['proposalSubtotal', 'proposal_subtotal'],
            proposalSalesTax: ['proposalSalesTax', 'proposal_sales_tax'],
            totalSale: ['totalSale', 'total_sale'],
            taxRate: ['taxRate', 'tax_rate'],
            county: ['county'],
            balanceDueOnProject: ['balanceDueOnProject', 'balance_due_on_project'],
            paymentType: ['paymentType', 'payment_type'],
        };

        const keys = fallbackMap[key] || [key];

        for (const currentKey of keys) {
            if (entry[currentKey] !== undefined && entry[currentKey] !== null) {
                return entry[currentKey];
            }
        }

        return '';
    }

    private normalizeEntry(entry: any): any {
        return {
            ...entry,
            clientName: this.getEntryValue(entry, 'clientName'),
            invoiceAmount: this.getEntryValue(entry, 'invoiceAmount'),
            invoicedSalesTax: this.getEntryValue(entry, 'invoicedSalesTax'),
            amountPaid: this.getEntryValue(entry, 'amountPaid'),
            paymentDate: this.getEntryValue(entry, 'paymentDate'),
            proposalSubtotal: this.getEntryValue(entry, 'proposalSubtotal'),
            proposalSalesTax: this.getEntryValue(entry, 'proposalSalesTax'),
            totalSale: this.getEntryValue(entry, 'totalSale'),
            taxRate: this.getEntryValue(entry, 'taxRate'),
            county: this.getEntryValue(entry, 'county'),
            balanceDueOnProject: this.getEntryValue(entry, 'balanceDueOnProject'),
            paymentType: this.getEntryValue(entry, 'paymentType'),
        };
    }

    private applyLocalControls(): void {
        let filteredEntries = [...this.allEntries];

        const searchValue = this.searchTerm.trim().toLowerCase();

        if (searchValue) {
            filteredEntries = filteredEntries.filter((entry) => {
                return [
                    entry.clientName,
                    entry.county,
                    entry.paymentType,
                    entry.paymentDate,
                ]
                    .filter(Boolean)
                    .some((value) =>
                        String(value).toLowerCase().includes(searchValue)
                    );
            });
        }

        filteredEntries.sort((a, b) => {
            const firstValue = this.getEntryValue(a, this.sortBy);
            const secondValue = this.getEntryValue(b, this.sortBy);

            const firstNumber = Number(firstValue);
            const secondNumber = Number(secondValue);

            let comparison = 0;

            if (!isNaN(firstNumber) && !isNaN(secondNumber)) {
                comparison = firstNumber - secondNumber;
            } else {
                comparison = String(firstValue || '').localeCompare(
                    String(secondValue || '')
                );
            }

            return this.sortDirection === 'ASC' ? comparison : comparison * -1;
        });

        this.entryCount = filteredEntries.length;

        const currentPage = this.tableControls.getCurrentPage();
        const limit = this.tableControls.getLimit();
        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;

        this.entries = filteredEntries.slice(startIndex, endIndex);
    }

    search(): void {
        this.tableControls.setCurrentPage(1);
        this.applyLocalControls();
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.tableControls.setCurrentPage(1);
        this.applyLocalControls();
    }

    clearDateRange(): void {
        this.setDefaultCurrentMonthRange();
        this.tableControls.setCurrentPage(1);
        this.getMonthlyPaymentsReport();
    }

    downloadReport(): void {
        const searchParams = new URLSearchParams();

        const formattedStartDate = this.formatDateForApi(this.startDate);
        const formattedEndDate = this.formatDateForApi(this.endDate);

        if (formattedStartDate) {
            searchParams.set('startDate', formattedStartDate);
        }

        if (formattedEndDate) {
            searchParams.set('endDate', formattedEndDate);
        }

        const url = new URL(
            '/api/reports/finances/clientMonthlyPayments/download?' +
            searchParams.toString(),
            environment.apiUrl
        );

        window.open(url.toString());
    }

    sorting(column: string): void {
        if (column !== this.sortBy) {
            this.sortDirection = 'ASC';
        } else {
            this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
        }

        this.sortBy = column;
        this.tableControls.setCurrentPage(1);
        this.applyLocalControls();
    }

    checkScreenSize(width?: number): void {
        const screenWidth = width || window.innerWidth;

        this.isMobileView = screenWidth <= 768;

        this.isMobileView
            ? this.tableControls.setLimit(5)
            : this.tableControls.setLimit(20);
    }
}